/**
 * VehicleArea — "is this car ready, and legal, to hand over?"
 *
 * FIVE OF THE SIX SERVER GATES LIVE HERE, and they are the ones with other people's calendars
 * attached: a DVLA registration is a fortnight, a roadworthiness test ten days, a service five.
 * That is why a route-less entry usually lands here — not because the area is important, but
 * because these are the clocks that have not started.
 *
 * THE ODOMETER IS LAST, AND THAT IS THE WHOLE REORDERING. The reading has to be the one taken when
 * the car is finally ready — after the workshop, after the plates, after the tracker. The
 * configuration this replaces listed it FIRST, which invited admins to record a number the workshop
 * then drove past by three hundred kilometres. No instrument prints it (the schedule's Vehicle
 * particulars stop at the condition); it is the handover baseline a later argument about wear,
 * mileage or valuation is settled against, and activation is held until it is recorded.
 *
 * FIVE SHEETS DIED HERE. Registration, roadworthiness, servicing, the tracker pairing and the
 * odometer were each a right-hand Sheet holding two or three fields, three of them containing a
 * SECOND sheet for the transaction. Every one of them is now the rows it always was, edited where
 * they are read, committed by the block's own bar. `RegistrationForm` and `TrackerForm` survive
 * because insights surfaces still mount them — as thin shells over the same writes this canvas
 * calls, so the two surfaces cannot disagree about what a registration is.
 *
 * EACH BLOCK IS ITS OWN COMPONENT, AND THAT IS A HOOK RULE RATHER THAN A PREFERENCE. A group
 * carries a reducer, a sync effect and a mutation; the service list carries one PER RECORD, and
 * hooks cannot be called in a loop. Splitting the blocks out is what lets the list be a list.
 *
 * `{{vehicle.mileage}}` READS `contracts.vehicle_mileage`, not a vehicle column. The help copy
 * here does not repeat the namespace's lie.
 *
 * THE ASSIGNMENT IS THE INVALIDATOR, AND IT IS NOW A CONTROL RATHER THAN A NOTE. Changing the car
 * voids the one instrument that names it, clears the odometer, and leaves three gates green while
 * describing the previous vehicle — so the affordance ships with all three facts on screen instead
 * of a paragraph promising a flow that had not landed. The rule itself lives in `../hazards`,
 * where a truth table can be tested; this file only draws it.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useDebounce } from '@/hooks/useDebounce';
import { REGISTRATION_TYPES, REGISTRATION_TYPE_LABELS } from '@/lib/registration';
import { createAgreement, sweepOrphans, voidAgreement } from '@/lib/agreements/api';
import type { ContractAgreementsModel } from '@/components/agreements/AgreementSlotList';

import { contractSetupKey, saveVehicleMileage } from '../data/contract';
import {
    contractMaintenanceKey,
    fetchContractMaintenance,
    saveMaintenance,
    type ContractMaintenance,
} from '../data/maintenance';
import {
    fetchVehicleRegistration,
    saveVehicleRegistration,
    vehicleRegistrationKey,
} from '../data/registration';
import { registrationSummary } from '../data/registrationDraft';
import {
    fieldErrorsFrom,
    ODOMETER_REFUSALS,
    REGISTRATION_REFUSALS,
    ROADWORTHINESS_REFUSALS,
    SERVICE_REFUSALS,
} from '../data/refusalFields';
import {
    contractRoadworthinessKey,
    fetchContractRoadworthiness,
    saveRoadworthiness,
} from '../data/roadworthiness';
import {
    SERVICE_TYPES,
    SERVICE_TYPE_LABELS,
    serviceSummary,
    serviceTypeLabel,
} from '../data/serviceDraft';
import {
    AVAILABLE_TRACKERS_KEY,
    availableTrackersKey,
    createTracker,
    fetchAvailableTrackers,
    fetchTracker,
    fetchVehicleTrackers,
    saveVehicleTracker,
    vehicleTrackersKey,
} from '../data/trackers';
import {
    availableTrackerRows,
    trackerLabel,
    trackerMeta,
    trackerSearchText,
    validateNewTracker,
    type PickableTracker,
} from '../data/trackerPick';
import { UNASSIGNED_TRANSACTIONS_KEY } from '../data/transactions';
import {
    AVAILABLE_VEHICLES_KEY,
    availableVehiclesKey,
    changeContractVehicle,
    fetchAvailableVehicles,
    type AvailableVehicle,
} from '../data/vehicles';
import HazardDialog from '../dossier/HazardDialog';
import { instrumentsFromSlots, vehicleChangeControl, type VehicleChangeControl } from '../hazards';
import InlineFieldGroup from '../inline/InlineFieldGroup';
import InlinePickerPopover, {
    PickerPanelActions,
    PickerPanelField,
    PickerPanelNote,
    type PickerRow,
} from '../inline/InlinePickerPopover';
import InlineUpload from '../inline/InlineUpload';
import QuietField from '../inline/QuietField';
import QuietSelect from '../inline/QuietSelect';
import TransactionPicker from '../inline/TransactionPicker';
import type { FieldValues } from '../inline/inlineEditReducer';
import { readUnanswered } from '../inline/readGate';
import { useInlineGroup } from '../inline/useInlineGroup';
import { useStagedFile } from '../inline/useStagedFile';
import type { PreparationItem } from '../useContractSetup';
import { Block, BlockActions, Blocks, Hint, Rec, Rows } from './blocks';

interface Props {
    contractId: number;
    vehicleId: number | undefined;
    vehicle: {
        make?: string | null;
        model?: string | null;
        color?: string | null;
        manufactured_at?: number | null;
        vin?: string | null;
    } | null;
    plate: string | null;
    /** `contracts.vehicle_mileage` — the handover reading, which no instrument prints. */
    mileage: number | null | undefined;
    /** The latest `contract_updates` status. Past DRAFT the assignment is fixed. */
    contractStatus: string | undefined;
    /** The page's one signing model — read for the instruments, used to void and reissue them. */
    agreements: ContractAgreementsModel;
    loading: boolean;
    getItemData: (item: string) => PreparationItem | undefined;
    onSuccess: () => void;
}

/**
 * The RPC's `tracker` details — and `tracker_id` IS THE `gps_trackers` PRIMARY KEY, emitted as a
 * JSON number. It is not an identifier anybody outside the database has ever seen, which is why
 * the rows below name the tracker by its serial instead and keep this only as a last resort.
 */
interface TrackerDetail {
    id: number;
    type: string | null;
    tracker_id: number | null;
}

function dateText(value: string | null | undefined): string {
    return value ? format(new Date(value), 'MMM d, yyyy') : '';
}

/** `yyyy-MM-dd`, which is what a native date input holds and what a `date` column takes. */
function dateValue(value: string | null | undefined): string {
    return value ? format(new Date(value), 'yyyy-MM-dd') : '';
}

// ─── Assignment ──────────────────────────────────────────────────────────────

/** What a car is called in a sentence: `Toyota Vitz 2018`. */
function vehicleName(vehicle: {
    make?: string | null;
    model?: string | null;
    manufactured_at?: number | null;
}): string {
    return [vehicle.make, vehicle.model, vehicle.manufactured_at].filter(Boolean).join(' ');
}

/**
 * The assignment block — the one control on this canvas that can invalidate the rest of it.
 *
 * THREE MODES, AND THE MODE IS THE WHOLE DESIGN (`../hazards`):
 *
 *  - REFUSED past DRAFT. The car a contract activated on is the car it names, and the screen says
 *    so rather than offering a control it would then have to take back. The database guards
 *    `driver_id` and `witness_id` this way and does not watch `vehicle_id` at all, so this refusal
 *    is the client's own and the sentence admits it.
 *  - HAZARD on DRAFT while an instrument names the car. There is no "change only": a signed
 *    document quietly keeping the old plate is precisely the laundering the backend comment warns
 *    about, so the only offered flow voids it, changes the car, and reissues from the new one.
 *  - OPEN otherwise. A plain picker over the same pool the contract wizard picks from.
 *
 * THE PICK IS NOT THE WRITE IN HAZARD MODE. Everywhere else on this canvas a pick is an act — a
 * relationship between two rows that already exist — but this one voids signatures, so the chosen
 * car is STAGED and the confirm is where it happens. Cancelling drops the staged car and nothing
 * has moved.
 */
function AssignmentBlock({
    contractId,
    vehicleId,
    vehicle,
    plate,
    mileage,
    control,
    agreements,
    onSaved,
}: {
    contractId: number;
    vehicleId: number | undefined;
    vehicle: Props['vehicle'];
    plate: string | null;
    /** Shown in the diff, because the change is what clears it. */
    mileage: number | null | undefined;
    control: VehicleChangeControl;
    agreements: ContractAgreementsModel;
    onSaved: () => void;
}) {
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [staged, setStaged] = useState<AvailableVehicle | null>(null);
    const [refusal, setRefusal] = useState<string | null>(null);

    /**
     * DEBOUNCED, LIKE THE WIZARD'S OWN VEHICLE SEARCH. `fetchAvailableVehicles` runs an `ilike`
     * across four columns of the whole fleet; keying the query on every keystroke fires one of
     * those per character typed, and the answers race — a slower early request can land after a
     * later one and repaint the list with results for a prefix nobody is looking at any more.
     * `SelectVehicleStep` has always held this at 300ms; this is the same pool through the same
     * key, so it holds it the same way.
     */
    const debouncedSearch = useDebounce(search, 300);

    const { data: pool, isLoading } = useQuery({
        queryKey: availableVehiclesKey(debouncedSearch),
        queryFn: () => fetchAvailableVehicles(debouncedSearch),
        staleTime: 30000,
        enabled: control.mode !== 'refused',
    });

    const change = useMutation({
        /**
         * VOID, THEN CHANGE, THEN REISSUE — and the order is the correctness of this mutation.
         *
         * Voiding first releases the arc's live-document slot; the partial unique index behind it
         * admits one non-VOIDED document per subject, so a `agreement_create` before the void would
         * simply hand back the document that names the old car. Changing second means the reissue
         * renders from the NEW assignment — reissuing before the write would freeze the previous
         * plate into a brand new document, which is the failure this whole flow exists to prevent.
         */
        mutationFn: async (next: AvailableVehicle) => {
            const reason =
                `Vehicle changed from ${plate || vehicleName(vehicle ?? {}) || 'the previous car'} to ` +
                `${next.registration_number || vehicleName(next) || `vehicle #${next.id}`}`;

            for (const instrument of control.voidable) {
                if (instrument.documentId === null) continue;
                await voidAgreement(instrument.documentId, reason);
            }

            await changeContractVehicle({ contractId, vehicleId: next.id });

            for (const instrument of control.voidable) {
                await createAgreement(instrument.docType, instrument.subjectId);
            }
        },
        onSuccess: () => {
            setStaged(null);
            setRefusal(null);
            // The contract row (the new car and the cleared odometer), the six gates, the pool this
            // car has just left, and the insights surface that draws the same assignment.
            queryClient.invalidateQueries({ queryKey: contractSetupKey(contractId) });
            queryClient.invalidateQueries({ queryKey: ['prep-status'] });
            queryClient.invalidateQueries({ queryKey: AVAILABLE_VEHICLES_KEY });
            queryClient.invalidateQueries({ queryKey: ['contract-insights'] });
            // Documents, arcs, subjects, tokens and the completion read, in one call.
            agreements.refreshBoard();
            // The bytes the voids above stranded [D-B44]. A DRAFT contract can hold a finalized
            // hire purchase agreement, so this gesture reaches an executed PDF. Fire and forget:
            // it never throws, reports nothing, and a change that voided nothing is answered with
            // an empty list rather than an error.
            sweepOrphans();
            onSaved();
        },
        // VERBATIM, ON THE BLOCK THAT RAISED IT — and inside the dialog while one is open, so a
        // refusal is read where the decision was taken.
        onError: (raised: Error) => setRefusal(raised.message || 'The change was refused.'),
    });

    const rows: PickerRow<AvailableVehicle>[] = (pool ?? []).map((candidate) => ({
        id: String(candidate.id),
        search: [
            candidate.registration_number,
            candidate.make,
            candidate.model,
            candidate.vin,
        ]
            .filter(Boolean)
            .join(' '),
        item: candidate,
    }));

    const carName = vehicleName(vehicle ?? {});
    const assigned = !!vehicle && (!!carName || !!plate);

    /**
     * How this car is named wherever this block names it: `Toyota Corolla · GT-1234-20`.
     *
     * ONE LINE, ONE PLACE. It used to be written three times over — once as the picker's own value
     * and again as `Make and model` and `Registration plate` beneath it — so an assigned car
     * printed its model twice and its plate twice on a canvas whose whole rule is that a row is
     * the record. The plate's editor is the Registration block's `Plate` field, and the plate
     * itself belongs here only as part of the car's name.
     */
    const vehicleLine = (
        <>
            {carName || 'Assigned'}
            {plate ? (
                <>
                    {' · '}
                    <span className="sd-num">{plate}</span>
                </>
            ) : null}
        </>
    );

    return (
        <Block id="b-assignment" title="Assignment">
            <p className="sd-lede sd-dim">
                {assigned
                    ? 'The car this contract is written over. Its model, year, colour and plate are four of the merge fields the hire purchase agreement needs — the schedule identifies the vehicle by all four. The odometer reading is required too, as the handover record rather than as anything the schedule prints.'
                    : 'No vehicle is assigned to this contract. The hire purchase agreement names the car it is written over, so it cannot be rendered until one is.'}
            </p>

            <Rows>
                {/*
                 * THE ROW IS STILL THE ROW WHERE IT CANNOT BE TOUCHED. A contract too far along to
                 * change its car loses the picker, not the fact — so the same line the trigger
                 * reads is drawn as a record instead, and the car is named exactly once either way.
                 */}
                {control.mode === 'refused' && (
                    <Rec
                        label="Vehicle"
                        value={assigned ? vehicleLine : null}
                        empty="No vehicle assigned"
                    />
                )}
                {control.mode !== 'refused' && (
                    <InlinePickerPopover<AvailableVehicle>
                        id="f-vehicle"
                        label="Vehicle"
                        rows={rows}
                        /*
                         * THE CURRENT CAR IS NEVER IN THE LIST, and that is the pool's own rule
                         * rather than an oversight: a vehicle held by a DRAFT contract is not
                         * available, and this contract is that DRAFT. So the id names what is
                         * assigned — which is what the trigger reads and what stops the row
                         * drawing itself as empty — and the list below offers only the free cars.
                         */
                        selectedId={vehicleId == null ? null : String(vehicleId)}
                        selectedLabel={vehicleLine}
                        // `vehicleLine` sets the plate in tabular figures, so it is a node and has
                        // no text to read back for the trigger's own tooltip. Same line, as text.
                        selectedTitle={
                            assigned ? [carName, plate].filter(Boolean).join(' · ') : undefined
                        }
                        emptyLabel="No vehicle assigned"
                        searchPlaceholder="Search by plate, VIN, make or model"
                        onQueryChange={setSearch}
                        noun="free vehicle"
                        renderLabel={(candidate) => (
                            <span className="sd-num">
                                {candidate.registration_number || `#${candidate.id}`}
                            </span>
                        )}
                        renderMeta={(candidate) =>
                            [vehicleName(candidate), candidate.vin].filter(Boolean).join(' · ')
                        }
                        rowTitle={(candidate) =>
                            [
                                candidate.registration_number || `#${candidate.id}`,
                                vehicleName(candidate),
                                candidate.vin,
                            ]
                                .filter(Boolean)
                                .join(' · ')
                        }
                        // 500, NOT 320: the plate leads at 77px and the meta behind it is a make,
                        // a model and a 17-character VIN — `Mercedes-Benz Sprinter ·
                        // WVWZZZ1JZ3W386752` is 279px — so a car could not be told apart from the
                        // next one by the two facts that tell them apart.
                        width={500}
                        onSelect={(row) => {
                            setRefusal(null);
                            if (control.mode === 'hazard') setStaged(row.item);
                            else change.mutate(row.item);
                        }}
                        loading={isLoading}
                        disabled={change.isPending}
                        error={staged ? null : refusal}
                        hint={
                            control.mode === 'hazard'
                                ? 'choosing one opens the confirm — nothing is written until then'
                                : 'cars on no live contract'
                        }
                    />
                )}
                {/* NOT IN THE LINE ABOVE, so they are rows of their own — and only where the car
                    actually carries them. */}
                {vehicle?.color && <Rec label="Colour" value={vehicle.color} />}
                <Rec
                    label="Chassis (VIN)"
                    value={vehicle?.vin ? <span className="sd-num">{vehicle.vin}</span> : null}
                    empty="Not recorded"
                />
            </Rows>

            <Hint className="mt-3">{control.reason}</Hint>

            {/*
             * THE ONE THING THIS SCREEN CANNOT UNDO. A paper original was signed off-system and
             * recorded here as a date and a URL; there is no document to void, so it goes on naming
             * whatever car it was signed over. The banner is not dismissible because the fact is not.
             */}
            {control.banner && (
                <div className="sd-banner">
                    <span className="sd-banner-mark" aria-hidden="true" />
                    <p>{control.banner}</p>
                </div>
            )}

            <HazardDialog
                open={staged !== null}
                onOpenChange={(open) => {
                    if (open) return;
                    setStaged(null);
                    setRefusal(null);
                }}
                title={`Change the vehicle on contract #${contractId}`}
                consequence={control.consequence ?? ''}
                fragment={
                    staged
                        ? {
                              old: `…the vehicle ${carName || 'described in the schedule'}${plate ? `, registered ${plate}` : ''}${vehicle?.vin ? `, chassis ${vehicle.vin}` : ''}…`,
                              now: `…the vehicle ${vehicleName(staged) || 'described in the schedule'}${staged.registration_number ? `, registered ${staged.registration_number}` : ''}${staged.vin ? `, chassis ${staged.vin}` : ''}…`,
                          }
                        : undefined
                }
                diffs={
                    staged
                        ? [
                              {
                                  label: 'Registration plate',
                                  old: plate || '—',
                                  now: staged.registration_number || '—',
                              },
                              {
                                  label: 'Chassis (VIN)',
                                  old: vehicle?.vin || '—',
                                  now: staged.vin || '—',
                              },
                              {
                                  label: 'Odometer at handover',
                                  old:
                                      mileage == null
                                          ? 'not recorded'
                                          : `${mileage.toLocaleString('en-GH')} km`,
                                  now: 'cleared — retake it on the new car',
                              },
                          ]
                        : []
                }
                collateral={control.collateral}
                confirmLabel={
                    control.voidable.length > 0
                        ? 'Change and void the agreement'
                        : 'Change the vehicle anyway'
                }
                cancelLabel={
                    control.voidable.length > 0
                        ? 'Keep the agreement and cancel'
                        : 'Keep this vehicle'
                }
                pending={change.isPending}
                error={refusal}
                onConfirm={() => staged && change.mutate(staged)}
            />
        </Block>
    );
}

// ─── Registration ────────────────────────────────────────────────────────────

/**
 * WHAT A COMMIT THAT BEAT THE REGISTRATION READ HOME IS TOLD — transient, and it says so. It goes
 * to the group's own line, like every refusal this block cannot attribute to one field.
 */
const REGISTRATION_READ_REFUSAL =
    'The registration for this vehicle is still loading. Try that again in a moment.';

/**
 * The registration block: four fields, one commit.
 *
 * THE GROUP SEEDS FROM THE REGISTRATION ROW, NOT FROM `get_preparation_status`. The RPC's details
 * carry the number and the type and nothing else — no certificate, no payment — and they are a
 * snapshot taken before the last save, so a group seeded from them opens on stale values the
 * moment an admin corrects a plate and reopens the block.
 */
function RegistrationBlock({
    vehicleId,
    complete,
    plate,
    onSaved,
}: {
    vehicleId: number;
    complete: boolean;
    plate: string | null;
    onSaved: () => void;
}) {
    const queryClient = useQueryClient();
    // OPEN WHEN THERE IS WORK IN IT. A registered car collapses to its plate; an unregistered one
    // is a fourteen-day external wait that has not started, and folding its four fields behind a
    // chevron is one more thing between an admin and the only move that matters here. The prep
    // status is resolved before this block is drawn, so the first render already knows which.
    const [open, setOpen] = useState(!complete);

    const { data: registration, isLoading } = useQuery({
        queryKey: vehicleRegistrationKey(vehicleId),
        queryFn: () => fetchVehicleRegistration(vehicleId),
        enabled: !!vehicleId,
    });

    /**
     * THIS BLOCK'S WRITES FAIL CLOSED UNTIL THIS BLOCK'S OWN READ HAS ANSWERED — and only until
     * then; nothing else on the canvas closes them.
     *
     * The four fields below are drawn from `registration`, which is a round trip of its own: the
     * area is already on screen, an unregistered car opens this block by default, and every field
     * takes typing while that read is still out. In that window the group holds a blank plate, a
     * blank payment and a `register` NOBODY chose, and `registration?.id` is null — so a commit
     * does not amend the registration, it inserts a second one and repoints the vehicle at it,
     * taking the recorded plate, certificate and payment out of view of every reader including
     * `get_preparation_status`. `readGate` holds the rule; the disabled rows below are the courtesy
     * and the guard on the write is the fix, because a draft typed a moment before the answer
     * landed can still reach Save.
     */
    const writesClosed = readUnanswered(registration);

    const server = useMemo<FieldValues>(
        () => ({
            type: registration?.type ?? 'register',
            number: registration?.number ?? '',
            transactionId:
                registration?.transaction_id == null ? '' : String(registration.transaction_id),
            certificate: '',
        }),
        [registration?.type, registration?.number, registration?.transaction_id],
    );

    const group = useInlineGroup({
        server,
        // THE SUBJECT IS THE VEHICLE, NOT THE ROW. A vehicle points at exactly one registration, so
        // there is nothing here to swap and rule 5 has nothing to fire on — whereas keying it to
        // the row id would make the first save (which CREATES the row) look like a change of
        // subject, resetting the group and taking its `Saved …` line with it a second after it
        // appeared. A change of vehicle does change this, which is exactly when a draft should go.
        subject: `registration:${vehicleId}`,
        fieldErrorsFor: fieldErrorsFrom(REGISTRATION_REFUSALS),
        save: (draft) => {
            if (writesClosed) throw new Error(REGISTRATION_READ_REFUSAL);
            return saveVehicleRegistration({
                vehicleId,
                registrationId: registration?.id ?? null,
                draft: {
                    type: draft.type,
                    number: draft.number,
                    transactionId: draft.transactionId,
                },
                certificateFile: certificate.file,
            });
        },
        onSaved: () => {
            // The sheet's own invalidations, unchanged: the gate, this read, and the queue the
            // linked payment has just left.
            queryClient.invalidateQueries({ queryKey: ['prep-status'] });
            queryClient.invalidateQueries({ queryKey: vehicleRegistrationKey(vehicleId) });
            queryClient.invalidateQueries({ queryKey: UNASSIGNED_TRANSACTIONS_KEY });
            onSaved();
        },
    });
    const certificate = useStagedFile(group, 'certificate');

    return (
        <Block id="b-registration" title="Registration">
            {complete ? (
                <p className="sd-lede sd-dim">
                    Registered. The plate prints on the hire purchase agreement, so a correction here
                    changes what an unsigned agreement will say.
                </p>
            ) : (
                <p className="sd-lede sd-dim">
                    Not registered yet. This is the longest external wait in the journey — start it
                    before anything that only costs typing.
                </p>
            )}

            <InlineFieldGroup
                group={group}
                summary={{
                    label: 'Registration',
                    value: isLoading
                        ? 'Loading…'
                        : registrationSummary({
                              number: group.draft.number || plate || '',
                              type: group.draft.type,
                          }),
                    open,
                    onToggle: () => setOpen((wasOpen) => !wasOpen),
                }}
                footnote="Required to activate. The number is a merge field of the hire purchase agreement."
            >
                <Rows>
                    <QuietField
                        {...group.field('number')}
                        id="f-regNumber"
                        label="Plate"
                        placeholder="GR-1234-24"
                        format="prints on the agreement"
                        onValueChange={(value) => group.set('number', value.toUpperCase())}
                        disabled={writesClosed}
                    />
                    <QuietSelect
                        {...group.field('type')}
                        id="f-regType"
                        label="Type"
                        options={REGISTRATION_TYPES.map((value) => ({
                            value,
                            label: REGISTRATION_TYPE_LABELS[value],
                        }))}
                        format="how this car came to be on the road"
                        disabled={writesClosed}
                    />
                    <TransactionPicker
                        id="f-regTxn"
                        label="Payment"
                        type="DEBIT"
                        value={group.draft.transactionId}
                        onChange={(value) => group.set('transactionId', value)}
                        dirty={group.field('transactionId').dirty}
                        error={group.field('transactionId').error}
                        disabled={writesClosed || group.saving}
                        emptyLabel="Not linked — required"
                        hint="the registration fees"
                    />
                    <InlineUpload
                        id="f-regCert"
                        label="Registration certificate"
                        current={{ url: registration?.certificate_url ?? null }}
                        file={certificate.file}
                        onFileChange={certificate.stage}
                        disabled={writesClosed || group.saving}
                        empty="Not on file · optional"
                    />
                </Rows>
            </InlineFieldGroup>
        </Block>
    );
}

// ─── Roadworthiness ──────────────────────────────────────────────────────────

/** The same sentence for the same window, about the record this block describes. */
const ROADWORTHINESS_READ_REFUSAL =
    'The roadworthiness record for this contract is still loading. Try that again in a moment.';

function RoadworthinessBlock({
    contractId,
    vehicleId,
    complete,
    onSaved,
}: {
    contractId: number;
    vehicleId: number;
    complete: boolean;
    onSaved: () => void;
}) {
    const queryClient = useQueryClient();

    const { data: record } = useQuery({
        queryKey: contractRoadworthinessKey(contractId),
        queryFn: () => fetchContractRoadworthiness(contractId),
        enabled: contractId > 0,
    });

    /**
     * AND THIS BLOCK AGAINST ITS OWN READ, WHICH IS A DIFFERENT ROUND TRIP. Registration's answer
     * says nothing about this record: `record?.id` is null until this query lands, and the
     * certificate URL it carries is what the write sends back for the document already on file. A
     * commit taken before it answers therefore inserts a SECOND certificate row against the
     * contract — and `get_preparation_status` picks the oldest, so the gate goes on reading the one
     * the admin thought they had just corrected.
     */
    const writesClosed = readUnanswered(record);

    const server = useMemo<FieldValues>(
        () => ({
            expiresAt: dateValue(record?.expiresAt),
            transactionId: record?.transactionId == null ? '' : String(record.transactionId),
            certificate: '',
        }),
        [record?.expiresAt, record?.transactionId],
    );

    const group = useInlineGroup({
        server,
        // The contract carries one certificate; creating it is not a change of subject. See the
        // registration block for why this is keyed to the owner rather than to the row.
        subject: `roadworthiness:${contractId}`,
        fieldErrorsFor: fieldErrorsFrom(ROADWORTHINESS_REFUSALS),
        save: (draft) => {
            if (writesClosed) throw new Error(ROADWORTHINESS_READ_REFUSAL);
            return saveRoadworthiness({
                roadworthinessId: record?.id ?? null,
                vehicleId,
                contractId,
                expiresAt: draft.expiresAt,
                certificateUrl: record?.certificateUrl ?? null,
                certificateFile: certificate.file,
                transactionId: draft.transactionId === '' ? null : Number(draft.transactionId),
                // What the record holds TODAY, which is what decides whether the key may be sent
                // at all before the backend migration lands. See `saveRoadworthiness`.
                linkedTransactionId: record?.transactionId ?? null,
            });
        },
        onSaved: () => {
            queryClient.invalidateQueries({ queryKey: ['prep-status'] });
            // Prefix form: the insights sheet's read is keyed by id and vehicle, this canvas's by
            // contract, and both have to come back with what was just saved.
            queryClient.invalidateQueries({ queryKey: ['vehicle-roadworthiness'] });
            queryClient.invalidateQueries({ queryKey: UNASSIGNED_TRANSACTIONS_KEY });
            onSaved();
        },
    });
    const certificate = useStagedFile(group, 'certificate');

    return (
        <Block id="b-roadworthiness" title="Roadworthiness">
            {complete ? (
                <p className="sd-lede sd-dim">
                    On file. The gate clears on the document; the payment beside it is a record.
                </p>
            ) : (
                <p className="sd-lede sd-dim">
                    The certificate comes from the inspector, usually a few days after the service
                    passes. Empty is normal — about 15 per cent of contracts have one on file at this
                    point. The gate clears when you record it.
                </p>
            )}

            <InlineFieldGroup
                group={group}
                footnote="Required to activate. The certificate is the gate; the payment is optional and always was."
            >
                <Rows>
                    <QuietField
                        {...group.field('expiresAt')}
                        id="f-rwDate"
                        label="Expires"
                        type="date"
                        format="the date on the certificate"
                        disabled={writesClosed}
                    />
                    <InlineUpload
                        id="f-rwCert"
                        label="Roadworthiness certificate"
                        current={{ url: record?.certificateUrl ?? null }}
                        file={certificate.file}
                        onFileChange={certificate.stage}
                        error={group.field('certificate').error}
                        disabled={writesClosed || group.saving}
                        empty="Not on file — required"
                    />
                    {/* OPTIONAL, AND THE ONLY OPTIONAL LINK ON THIS BLOCK. A certificate paid for
                        out of a payment nobody has matched yet is still a certificate — which is
                        why this row, alone among the transaction rows on the canvas, offers a way
                        back to nothing. */}
                    <TransactionPicker
                        id="f-rwTxn"
                        label="Payment"
                        type="DEBIT"
                        value={group.draft.transactionId}
                        onChange={(value) => group.set('transactionId', value)}
                        onClear={() => group.set('transactionId', '')}
                        dirty={group.field('transactionId').dirty}
                        error={group.field('transactionId').error}
                        disabled={writesClosed || group.saving}
                        emptyLabel="Not linked · optional"
                        hint="linking it takes the payment out of the unassigned queue"
                    />
                </Rows>
            </InlineFieldGroup>
        </Block>
    );
}

// ─── Service and conversion ──────────────────────────────────────────────────

const SERVICE_OPTIONS = SERVICE_TYPES.map((value) => ({
    value,
    label: SERVICE_TYPE_LABELS[value],
}));

/** One workshop record — its own group, because a list of groups is a list of reducers. */
function ServiceRow({
    record,
    vehicleId,
    contractId,
    onSaved,
    onDone,
}: {
    /** Null is the add row: same fields, same commit, no summary to collapse into. */
    record: ContractMaintenance | null;
    vehicleId: number;
    contractId: number;
    onSaved: () => void;
    /** Only the add row has one — it closes itself once the record exists. */
    onDone?: () => void;
}) {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(record === null);

    const server = useMemo<FieldValues>(
        () => ({
            type: record?.type ?? 'servicing',
            description: record?.description ?? '',
            startedAt: dateValue(record?.started_at),
            endedAt: dateValue(record?.ended_at),
            transactionId: record?.transaction_id == null ? '' : String(record.transaction_id),
        }),
        [
            record?.type,
            record?.description,
            record?.started_at,
            record?.ended_at,
            record?.transaction_id,
        ],
    );

    const group = useInlineGroup({
        server,
        subject: record ? `service:${record.id}` : 'service:new',
        fieldErrorsFor: fieldErrorsFrom(SERVICE_REFUSALS),
        save: (draft) =>
            saveMaintenance({
                maintenanceId: record?.id ?? null,
                vehicleId,
                contractId,
                draft: {
                    type: draft.type,
                    description: draft.description,
                    startedAt: draft.startedAt,
                    endedAt: draft.endedAt,
                    transactionId: draft.transactionId,
                },
            }),
        onSaved: () => {
            queryClient.invalidateQueries({ queryKey: ['prep-status'] });
            queryClient.invalidateQueries({ queryKey: contractMaintenanceKey(contractId) });
            queryClient.invalidateQueries({ queryKey: UNASSIGNED_TRANSACTIONS_KEY });
            onSaved();
            onDone?.();
        },
    });

    const prefix = record ? `f-svc${record.id}` : 'f-svcNew';

    return (
        <InlineFieldGroup
            group={group}
            summary={
                record
                    ? {
                          label: record.started_at
                              ? dateText(record.started_at)
                              : serviceTypeLabel(record.type),
                          value: serviceSummary(record),
                          open,
                          onToggle: () => setOpen((wasOpen) => !wasOpen),
                      }
                    : undefined
            }
            footnote={
                record
                    ? undefined
                    : 'The amount comes off the payment you link — there is no separate figure to type.'
            }
        >
            <Rows>
                <QuietSelect
                    {...group.field('type')}
                    id={`${prefix}Type`}
                    label="Type"
                    options={SERVICE_OPTIONS}
                    format="both clear the activation gate"
                />
                <QuietField
                    {...group.field('description')}
                    id={`${prefix}Desc`}
                    label="Work done"
                    placeholder="What the workshop did"
                />
                <QuietField
                    {...group.field('startedAt')}
                    id={`${prefix}Start`}
                    label="Started"
                    type="date"
                />
                <QuietField
                    {...group.field('endedAt')}
                    id={`${prefix}End`}
                    label="Finished"
                    type="date"
                    format="leave empty while it is still in the workshop"
                />
                <TransactionPicker
                    id={`${prefix}Txn`}
                    label="Payment"
                    type="DEBIT"
                    value={group.draft.transactionId}
                    onChange={(value) => group.set('transactionId', value)}
                    dirty={group.field('transactionId').dirty}
                    error={group.field('transactionId').error}
                    disabled={group.saving}
                    emptyLabel="Not linked — required"
                    hint="the amount on the record is this payment's"
                />
            </Rows>
            {!record && (
                <BlockActions>
                    <Button variant="ghost" size="sm" onClick={onDone}>
                        Cancel this record
                    </Button>
                </BlockActions>
            )}
        </InlineFieldGroup>
    );
}

function ServiceBlock({
    vehicleId,
    contractId,
    complete,
    onSaved,
}: {
    vehicleId: number;
    contractId: number;
    complete: boolean;
    onSaved: () => void;
}) {
    const [adding, setAdding] = useState(false);

    /**
     * BY CONTRACT, NOT BY VEHICLE. `get_preparation_status` counts rows against the contract, and
     * the sheet this replaces read them against the vehicle with `.limit(1)` — so a car that had
     * been through the workshop for a previous contract showed that job here.
     */
    const { data: records, isLoading } = useQuery({
        queryKey: contractMaintenanceKey(contractId),
        queryFn: () => fetchContractMaintenance(contractId),
        enabled: contractId > 0,
    });

    const rows = records ?? [];

    return (
        <Block id="b-service" title="Service and conversion">
            {isLoading ? (
                <p className="sd-lede sd-dim">Loading…</p>
            ) : rows.length === 0 ? (
                <p className="sd-lede sd-dim">
                    Nothing recorded. Required to activate — the gate clears when at least one
                    servicing or conversion job is on this contract.
                </p>
            ) : (
                <p className="sd-lede sd-dim">
                    {rows.length === 1 ? 'One job' : `${rows.length} jobs`} on this contract.
                    {complete ? ' The gate is clear.' : ''} A car can go through the workshop more
                    than once before it is handed over — this is the only prep item that is a list.
                </p>
            )}

            <Rows>
                {rows.map((record) => (
                    <ServiceRow
                        key={record.id}
                        record={record}
                        vehicleId={vehicleId}
                        contractId={contractId}
                        onSaved={onSaved}
                    />
                ))}
            </Rows>

            {adding ? (
                <div className="mt-4">
                    <ServiceRow
                        record={null}
                        vehicleId={vehicleId}
                        contractId={contractId}
                        onSaved={onSaved}
                        onDone={() => setAdding(false)}
                    />
                </div>
            ) : (
                <BlockActions>
                    <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
                        {rows.length > 0 ? 'Add a job' : 'Record servicing'}
                    </Button>
                </BlockActions>
            )}
        </Block>
    );
}

// ─── Trackers ────────────────────────────────────────────────────────────────

/**
 * One tracker slot, as a picker row.
 *
 * SEPARATED FROM THE GROUP'S FIELDS BECAUSE THE POOL IS A QUERY. Both slots read the same
 * available-trackers list and each has to hide whatever the other holds, so the search text they
 * type is shared state and the exclusion is computed from the group's own draft.
 */
function TrackerSlot({
    id,
    label,
    value,
    onChange,
    onClear,
    excludeId,
    dirty,
    error,
    disabled,
    hint,
    emptyLabel,
}: {
    id: string;
    label: string;
    value: string;
    onChange: (trackerId: string) => void;
    onClear?: () => void;
    excludeId: number | null;
    dirty?: boolean;
    error?: string | null;
    disabled?: boolean;
    hint: string;
    emptyLabel: string;
}) {
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [refusal, setRefusal] = useState<string | null>(null);
    const selectedId = value.trim() === '' ? null : Number(value);

    const { data: pool, isLoading } = useQuery({
        queryKey: availableTrackersKey(search),
        queryFn: () => fetchAvailableTrackers(search),
        staleTime: 30000,
    });

    const { data: current } = useQuery({
        queryKey: ['tracker', selectedId],
        queryFn: () => fetchTracker(selectedId),
        enabled: selectedId !== null,
        staleTime: 60000,
    });

    const create = useMutation({
        mutationFn: (draft: { serial: string; sim: string }) => createTracker(draft),
        onSuccess: (tracker) => {
            queryClient.invalidateQueries({ queryKey: AVAILABLE_TRACKERS_KEY });
            queryClient.setQueryData(['tracker', tracker.id], tracker);
            onChange(String(tracker.id));
            setRefusal(null);
        },
        onError: (raised: Error) => setRefusal(raised.message || 'The tracker was refused.'),
    });

    const rows: PickerRow<PickableTracker>[] = availableTrackerRows(pool, current, excludeId).map(
        (tracker) => ({
            id: String(tracker.id),
            search: trackerSearchText(tracker),
            item: tracker,
        }),
    );
    const selected = rows.find((row) => row.id === value)?.item ?? current ?? null;

    return (
        <InlinePickerPopover<PickableTracker>
            id={id}
            label={label}
            rows={rows}
            selectedId={selectedId === null ? null : String(selectedId)}
            selectedLabel={selected ? trackerLabel(selected) : emptyLabel}
            emptyLabel={emptyLabel}
            searchPlaceholder="Search by serial or SIM"
            onQueryChange={setSearch}
            noun="tracker"
            renderLabel={(tracker) => <span className="sd-num">{trackerLabel(tracker)}</span>}
            renderMeta={trackerMeta}
            rowTitle={(tracker) => `${trackerLabel(tracker)} · ${trackerMeta(tracker)}`}
            // 380: the two numbers printed on the box. A 15-digit serial is 126px and `SIM
            // 0244123456` is 98px, which 320 does hold — but only just, and a longer serial had
            // nowhere to go. This is the narrowest picker on the canvas and it is meant to be.
            width={380}
            onSelect={(row) => onChange(row.id)}
            onClear={onClear}
            clearLabel="No tracker in this slot"
            loading={isLoading}
            disabled={disabled}
            dirty={dirty}
            error={error}
            hint={hint}
            create={{
                noun: 'tracker',
                requirement: 'Only for a box that is not on the system yet.',
                render: ({ typed, close, cancel }) => (
                    <NewTrackerPanel
                        id={id}
                        typed={typed}
                        pending={create.isPending}
                        refusal={refusal}
                        onCancel={cancel}
                        onCreate={(draft) => create.mutate(draft, { onSuccess: () => close() })}
                    />
                ),
            }}
        />
    );
}

/** Seeded once from whatever failed to match — see `TransactionPicker` for why this is a component. */
function NewTrackerPanel({
    id,
    typed,
    pending,
    refusal,
    onCreate,
    onCancel,
}: {
    id: string;
    typed: string;
    pending: boolean;
    refusal: string | null;
    onCreate: (draft: { serial: string; sim: string }) => void;
    onCancel: () => void;
}) {
    const [serial, setSerial] = useState(typed);
    const [sim, setSim] = useState('');
    const invalid = validateNewTracker({ serial });

    return (
        <>
            <PickerPanelField id={`${id}-new-serial`} label="Serial number">
                <input
                    id={`${id}-new-serial`}
                    value={serial}
                    onChange={(event) => setSerial(event.target.value)}
                    placeholder="Printed on the box"
                />
            </PickerPanelField>
            <PickerPanelField id={`${id}-new-sim`} label="SIM number">
                <input
                    id={`${id}-new-sim`}
                    inputMode="tel"
                    value={sim}
                    onChange={(event) => setSim(event.target.value)}
                    placeholder="Optional"
                />
            </PickerPanelField>
            {refusal && <PickerPanelNote bad>{refusal}</PickerPanelNote>}
            <PickerPanelActions>
                <Button variant="ghost" size="sm" onClick={onCancel}>
                    Back
                </Button>
                <Button
                    size="sm"
                    disabled={!!invalid || pending}
                    onClick={() => onCreate({ serial, sim })}
                >
                    Add it
                </Button>
            </PickerPanelActions>
        </>
    );
}

/**
 * The two slots.
 *
 * A PAIRING IS AN ACT, NOT A DRAFT — and that is the one place on this canvas where a pick writes
 * on its own. Every typed value here goes through a commit bar, because typed values print onto
 * legal instruments and a lost keystroke is not cosmetic. A tracker pairing prints on nothing: it
 * is one foreign key pointed at one box, it has an undo in the same row that made it, and the
 * witness and policy pickers this page already ships behave the same way. What commits and what
 * acts is therefore not a coin toss: **a value that will be printed is committed; a relationship
 * between two rows that already exist is an act.** The transaction rows inside the registration,
 * roadworthiness and service groups are the honest exception — they are part of a RECORD being
 * written, so they commit with the rest of it.
 */
function TrackerBlock({
    vehicleId,
    onSaved,
}: {
    vehicleId: number;
    onSaved: () => void;
}) {
    const queryClient = useQueryClient();

    const { data: paired, isLoading } = useQuery({
        queryKey: vehicleTrackersKey(vehicleId),
        queryFn: () => fetchVehicleTrackers(vehicleId),
        enabled: !!vehicleId,
    });

    const rows = (paired ?? []) as unknown as TrackerDetail[];
    const primary = rows.find((row) => row.type === 'primary') ?? null;
    const secondary = rows.find((row) => row.type !== 'primary') ?? null;

    const pair = useMutation({
        mutationFn: (input: {
            type: 'primary' | 'secondary';
            existingPairingId: number | null;
            trackerId: number | null;
        }) => saveVehicleTracker({ vehicleId, ...input }),
        onSuccess: () => {
            // The sheet's own four invalidations, unchanged.
            queryClient.invalidateQueries({ queryKey: ['prep-status'] });
            queryClient.invalidateQueries({ queryKey: AVAILABLE_TRACKERS_KEY });
            queryClient.invalidateQueries({ queryKey: vehicleTrackersKey(vehicleId) });
            queryClient.invalidateQueries({ queryKey: ['contract-insights'] });
            onSaved();
        },
    });

    // VERBATIM, ON THE ROW THAT RAISED IT. A pairing refused by the database says so where the
    // pick happened rather than in a toast that outlives the surface it was about.
    const refusalFor = (type: 'primary' | 'secondary') =>
        pair.isError && pair.variables?.type === type
            ? (pair.error as Error).message || 'The pairing was refused.'
            : null;

    return (
        <Block id="b-trackers" title="Trackers">
            <p className="sd-lede sd-dim">
                The primary tracker is required to activate. The secondary is a spare and gates
                nothing. A pairing takes effect as soon as it is picked — there is nothing here that
                prints, and the row that made it can undo it.
            </p>
            <Rows>
                <TrackerSlot
                    id="f-trackerPrimary"
                    label="Primary"
                    value={primary?.tracker_id == null ? '' : String(primary.tracker_id)}
                    onChange={(value) =>
                        pair.mutate({
                            type: 'primary',
                            existingPairingId: primary?.id ?? null,
                            trackerId: Number(value),
                        })
                    }
                    onClear={() =>
                        pair.mutate({
                            type: 'primary',
                            existingPairingId: primary?.id ?? null,
                            trackerId: null,
                        })
                    }
                    excludeId={secondary?.tracker_id ?? null}
                    error={refusalFor('primary')}
                    disabled={pair.isPending || isLoading}
                    emptyLabel="Not paired — required to activate"
                    hint="required to activate"
                />
                <TrackerSlot
                    id="f-trackerSecondary"
                    label="Secondary"
                    value={secondary?.tracker_id == null ? '' : String(secondary.tracker_id)}
                    onChange={(value) =>
                        pair.mutate({
                            type: 'secondary',
                            existingPairingId: secondary?.id ?? null,
                            trackerId: Number(value),
                        })
                    }
                    onClear={() =>
                        pair.mutate({
                            type: 'secondary',
                            existingPairingId: secondary?.id ?? null,
                            trackerId: null,
                        })
                    }
                    excludeId={primary?.tracker_id ?? null}
                    error={refusalFor('secondary')}
                    disabled={pair.isPending || isLoading}
                    emptyLabel="None — not required"
                    hint="optional spare"
                />
            </Rows>
        </Block>
    );
}

// ─── Odometer ────────────────────────────────────────────────────────────────

function OdometerBlock({
    contractId,
    mileage,
    onSaved,
}: {
    contractId: number;
    mileage: number | null | undefined;
    onSaved: () => void;
}) {
    const queryClient = useQueryClient();

    const server = useMemo<FieldValues>(
        () => ({ mileage: mileage == null ? '' : String(mileage) }),
        [mileage],
    );

    const group = useInlineGroup({
        server,
        fieldErrorsFor: fieldErrorsFrom(ODOMETER_REFUSALS),
        save: (draft) => saveVehicleMileage(contractId, draft.mileage),
        onSaved: () => {
            queryClient.invalidateQueries({ queryKey: ['contract-setup'] });
            queryClient.invalidateQueries({ queryKey: ['prep-status'] });
            onSaved();
        },
    });

    return (
        <Block id="b-odometer" title="Odometer at handover">
            <p className="sd-lede sd-dim">
                The handover record — what a later argument about wear, mileage or valuation is
                settled against. Take it when the car is finally ready, not when it was assigned.
            </p>
            <InlineFieldGroup group={group}>
                <Rows>
                    <QuietField
                        {...group.field('mileage')}
                        id="f-odometer"
                        label="Reading"
                        inputMode="numeric"
                        placeholder="—"
                        format="km"
                    />
                </Rows>
            </InlineFieldGroup>
        </Block>
    );
}

// ─── The area ────────────────────────────────────────────────────────────────

export default function VehicleArea({
    contractId,
    vehicleId,
    vehicle,
    plate,
    mileage,
    contractStatus,
    agreements,
    loading,
    getItemData,
    onSuccess,
}: Props) {
    const service = getItemData('service');
    const registration = getItemData('registration');
    const roadworthiness = getItemData('roadworthiness');
    const insurance = getItemData('insurance');

    /**
     * THE GATE IS COMPUTED BEFORE THE EARLY RETURN, because hooks are. `useMemo` below the loading
     * branch would be called on some renders and not others, and the block underneath would take
     * the reducer's place in the hook order the first time preparation finished loading.
     */
    const control = useMemo(
        () =>
            vehicleChangeControl({
                contractId,
                status: contractStatus ?? 'DRAFT',
                instruments: instrumentsFromSlots(agreements.slots),
                prepComplete: {
                    service: !!service?.is_complete,
                    roadworthiness: !!roadworthiness?.is_complete,
                    insurance: !!insurance?.is_complete,
                },
                hasVehicle: !!vehicleId,
            }),
        [
            contractId,
            contractStatus,
            agreements.slots,
            service?.is_complete,
            roadworthiness?.is_complete,
            insurance?.is_complete,
            vehicleId,
        ],
    );

    if (loading) {
        return (
            <div className="flex justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <Blocks>
            <AssignmentBlock
                contractId={contractId}
                vehicleId={vehicleId}
                vehicle={vehicle}
                plate={plate}
                mileage={mileage}
                control={control}
                agreements={agreements}
                onSaved={onSuccess}
            />

            {vehicleId ? (
                <>
                    <ServiceBlock
                        vehicleId={vehicleId}
                        contractId={contractId}
                        complete={!!service?.is_complete}
                        onSaved={onSuccess}
                    />
                    <RegistrationBlock
                        vehicleId={vehicleId}
                        complete={!!registration?.is_complete}
                        plate={plate}
                        onSaved={onSuccess}
                    />
                    <RoadworthinessBlock
                        contractId={contractId}
                        vehicleId={vehicleId}
                        complete={!!roadworthiness?.is_complete}
                        onSaved={onSuccess}
                    />
                    <TrackerBlock vehicleId={vehicleId} onSaved={onSuccess} />
                </>
            ) : (
                <Block id="b-service" title="Preparation">
                    <p className="sd-lede sd-dim">
                        Servicing, registration, roadworthiness and the trackers are all recorded
                        against a car. They open once one is assigned.
                    </p>
                </Block>
            )}

            {/* LAST, AND THAT IS THE POINT. */}
            <OdometerBlock contractId={contractId} mileage={mileage} onSaved={onSuccess} />
        </Blocks>
    );
}
